import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { UserManager } from 'oidc-client-ts';
import logger from '../utils/logger';
import config from '../config';

// Extend axios config to include metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      requestId: string;
      startTime: number;
    };
  }
}

// API Error types based on CLAUDE.md specifications
export interface APIError {
  error: {
    code: string;
    message: string;
    details?: any;
    request_id: string;
  };
}

export interface APIResponse<T = any> {
  data: T;
  meta?: {
    request_id: string;
    timestamp: string;
    total_count?: number;
    page_count?: number;
    page?: number;
    limit?: number;
  };
}

class ApiClient {
  private client: AxiosInstance;
  private userManager: UserManager | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: config.apiUrl,
      timeout: config.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Version': config.version,
      },
    });

    this.setupInterceptors();
  }

  public setUserManager(userManager: UserManager): void {
    this.userManager = userManager;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      async (config) => {
        const requestId = uuidv4();
        
        // Add request ID for tracing
        config.headers = config.headers || {};
        config.headers['X-Request-ID'] = requestId;

        // Add auth token if available
        if (this.userManager) {
          try {
            const user = await this.userManager.getUser();
            if (user && !user.expired) {
              config.headers['Authorization'] = `Bearer ${user.access_token}`;
            }
          } catch (error) {
            logger.error('Failed to get user token for request', {
              error: error instanceof Error ? error.message : 'Unknown error',
              requestId,
              url: config.url,
            });
          }
        }

        // Log API call start
        const startTime = Date.now();
        config.metadata = { requestId, startTime };

        logger.debug('API request started', {
          requestId,
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
        });

        return config;
      },
      (error) => {
        logger.error('Request interceptor error', {
          error: error.message,
        });
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        const { config } = response;
        const duration = config.metadata?.startTime 
          ? Date.now() - config.metadata.startTime 
          : undefined;

        logger.apiCall(
          config.method?.toUpperCase() || 'GET',
          config.url || '',
          duration,
          response.status
        );

        return response;
      },
      async (error: AxiosError) => {
        const { config } = error;
        const duration = config?.metadata?.startTime 
          ? Date.now() - config.metadata.startTime 
          : undefined;

        logger.error('API request failed', {
          requestId: config?.metadata?.requestId,
          method: config?.method?.toUpperCase(),
          url: config?.url,
          status: error.response?.status,
          duration_ms: duration,
          error: error.message,
        });

        // Handle 401 - Unauthorized (token expired)
        if (error.response?.status === 401 && this.userManager && config) {
          return this.handleTokenRefresh(error, config);
        }

        // Handle 403 - Forbidden
        if (error.response?.status === 403) {
          logger.authEvent('access_forbidden', {
            url: config?.url,
            method: config?.method,
          });
        }

        // Handle 429 - Rate Limited
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'];
          logger.warn('Rate limited', {
            url: config?.url,
            retry_after: retryAfter,
          });
          
          // Could implement automatic retry with exponential backoff here
        }

        return Promise.reject(this.transformError(error));
      }
    );
  }

  private async handleTokenRefresh(
    originalError: AxiosError, 
    originalConfig: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    if (!this.userManager) {
      return Promise.reject(originalError);
    }

    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      const newToken = await this.refreshPromise;
      if (newToken) {
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers['Authorization'] = `Bearer ${newToken}`;
        return this.client(originalConfig);
      }
      return Promise.reject(originalError);
    }

    this.isRefreshing = true;
    this.refreshPromise = this.refreshToken();

    try {
      const newToken = await this.refreshPromise;
      if (newToken) {
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers['Authorization'] = `Bearer ${newToken}`;
        return this.client(originalConfig);
      }
    } catch (refreshError) {
      logger.error('Token refresh failed', {
        error: refreshError instanceof Error ? refreshError.message : 'Unknown error',
      });
      
      // Redirect to login on refresh failure
      window.location.href = '/login';
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }

    return Promise.reject(originalError);
  }

  private async refreshToken(): Promise<string | null> {
    if (!this.userManager) {
      return null;
    }

    try {
      const user = await this.userManager.signinSilent();
      if (user) {
        logger.authEvent('token_refreshed', {
          userId: user.profile.sub,
        });
        return user.access_token;
      }
    } catch (error) {
      logger.error('Silent token refresh failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    return null;
  }

  private transformError(error: AxiosError): Error {
    if (error.response?.data) {
      const apiError = error.response.data as APIError;
      if (apiError.error) {
        const customError = new Error(apiError.error.message);
        customError.name = apiError.error.code;
        return customError;
      }
    }

    return new Error(error.message || 'Network error occurred');
  }

  // HTTP Methods
  public async get<T = any>(
    url: string, 
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.get<APIResponse<T>>(url, config);
    return response.data.data;
  }

  public async post<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.post<APIResponse<T>>(url, data, config);
    return response.data.data;
  }

  public async put<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.put<APIResponse<T>>(url, data, config);
    return response.data.data;
  }

  public async patch<T = any>(
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.patch<APIResponse<T>>(url, data, config);
    return response.data.data;
  }

  public async delete<T = any>(
    url: string, 
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.client.delete<APIResponse<T>>(url, config);
    return response.data.data;
  }

  // Raw response methods (for accessing meta data)
  public async getRaw<T = any>(
    url: string, 
    config?: AxiosRequestConfig
  ): Promise<APIResponse<T>> {
    const response = await this.client.get<APIResponse<T>>(url, config);
    return response.data;
  }

  // File upload helper
  public async uploadFile<T = any>(
    url: string, 
    file: File, 
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<APIResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });

    return response.data.data;
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
export { ApiClient };